/**
 * How many travelers the fare on screen was priced for, and whether the
 * travelers entered at checkout match it.
 *
 * Why (2026-10-27): a fare is priced for the travelers the customer SEARCHED
 * for, but checkout restores the travelers SAVED in the booking session, and
 * those survive a change of flights. Review's "searched" list also fell back to
 * the saved travelers whenever navigation state didn't carry the search (a
 * refresh, a cart resume). So three travelers saved from an earlier cart rode
 * onto a one-seat fare and were charged as one: C4J47L (Denaya, 3 travelers,
 * $357 Delta fare for 1). XG41DJ had 7 travelers on a 5-traveler search.
 *
 * The priced list comes ONLY from the search: navigation state, then the
 * booking session's saved search, then the search stored on the offer. It
 * never comes from the entered travelers.
 */

const nonEmpty = (v) => (Array.isArray(v) && v.length > 0 ? v : null);

/** The searched traveler list ([{ type }]) the fare was priced for, or null when unknown. */
export function pricedTravelerList({ statePassengers = null, contextSearchParams = null, selectedFlight = null } = {}) {
  return nonEmpty(statePassengers)
    || nonEmpty(contextSearchParams?.passengers)
    || nonEmpty(selectedFlight?._searchParams?.passengers)
    || null;
}

/**
 * Why the entered travelers can't be paid for as they stand, or null when they can.
 *   - 'mismatch': the fare is priced for a known count and the entered count differs
 *   - 'unknown_multi': the priced count is unknown and more than one traveler is
 *     entered, so the fare may cover fewer people than are listed
 */
export function travelerCountProblem(enteredCount, pricedCount) {
  const entered = Number(enteredCount) || 0;
  const priced = Number(pricedCount) || 0;
  if (entered === 0) return null;
  if (priced > 0) return entered === priced ? null : 'mismatch';
  return entered > 1 ? 'unknown_multi' : null;
}

/** Fit an entered traveler list to the priced count: keep the first ones, pad with blanks. */
export function fitTravelersToPricedCount(travelers, pricedCount, makeBlank) {
  const list = Array.isArray(travelers) ? travelers : [];
  const n = Math.max(1, Number(pricedCount) || 1);
  if (list.length === n) return list;
  if (list.length > n) return list.slice(0, n);
  return [...list, ...Array.from({ length: n - list.length }, (_, i) => makeBlank(list.length + i))];
}

/** One line for the customer: what doesn't match and what to do. */
export function travelerCountMessage(problem, enteredCount, pricedCount) {
  const people = (n) => `${n} traveler${n === 1 ? '' : 's'}`;
  if (problem === 'mismatch') {
    return `This price is for ${people(pricedCount)}, but ${people(enteredCount)} are entered. Update your travelers to ${pricedCount}, or search again for ${people(enteredCount)} to see the right price.`;
  }
  if (problem === 'unknown_multi') {
    return `We couldn't confirm how many travelers this price covers. Please search again for ${people(enteredCount)} so the price includes everyone.`;
  }
  return null;
}
